import { TransactionApiUrls } from "@/modules/transaction/common/enum";
import type {
  IBudgetAccount,
  IBudgetTransfer,
  ICreateBudgetTransferRequest,
  IRegionCodesResponse,
  ISaveAsTemplate,
  IUpdateBudgetTransferRequest,
  TTaxPayerResponse,
} from "@/modules/transaction/common/types";
import type { ICommission, IObject } from "@/shared/common/types";
import { generateQuery, withIdempotency } from "@/shared/helpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const budgetTransferApi = createApi({
  reducerPath: "budgetTransferApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    createBudgetTransfer: builder.mutation<string, ICreateBudgetTransferRequest>(
      withIdempotency<ICreateBudgetTransferRequest>(
        TransactionApiUrls.BudgetTransferV2
      )),
    updateBudgetTransfer: builder.mutation<string, IUpdateBudgetTransferRequest>({
      query: ({ data, id }) => ({
        url: `${TransactionApiUrls.BudgetTransferV2}/${id}`,
        method: "PUT",
        data,
      }),
    }),
    getBudgetTransfer: builder.query<IBudgetTransfer, {
      id: string
    }>({
      query: ({ id }) => ({
        url: `${TransactionApiUrls.BudgetTransfer}/${id}`,
      }),
    }),
    getBudgetTransferCommission: builder.query<ICommission, IObject>({
      query: (data) => {
        const query = generateQuery(data);

        return ({
          url: `${TransactionApiUrls.BudgetTransferCommission}?${query}`,
        });
      },
    }),
    getBudgetAccount: builder.query<IBudgetAccount, string>({
      query: (accountNumber) => ({
        url: `${TransactionApiUrls.BudgetAccounts}?accountNumber=${accountNumber}`
      }),
    }),
    getRegionCodes: builder.query<IRegionCodesResponse, string>({
      query: (regionCodeType) => ({
        url: `${TransactionApiUrls.RegionCodes}?regionCodeType=${regionCodeType}`
      }),
    }),
    getTaxPayers: builder.query<TTaxPayerResponse, string>({
      query: (tin) => ({
        url: `${TransactionApiUrls.TaxPayers}?tin=${tin}`
      }),
    }),
    saveBudgetTransferAsTemplate: builder.mutation<string, ISaveAsTemplate>({
      query: ({ id, name, idempotencyKey }) => ({
        url: `${TransactionApiUrls.BudgetTransfer}/${id}/Templates`,
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        data: { name },
      }),
    }),
  }),
});

export const {
  useLazyGetTaxPayersQuery,
  useGetBudgetTransferQuery,
  useLazyGetRegionCodesQuery,
  useLazyGetBudgetAccountQuery,
  useCreateBudgetTransferMutation,
  useUpdateBudgetTransferMutation,
  useLazyGetBudgetTransferCommissionQuery,
  useSaveBudgetTransferAsTemplateMutation,
} = budgetTransferApi;
